import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Pencil, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import { LicenseForm } from "../components/forms";
import PrintButton from "../components/PrintButton";
import PrintHeader from "../components/PrintHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LIC_CATEGORIES, LICENSE_STATUTS, STATUT_LABELS } from "../lib/constants";

export default function Licenses() {
  const { hasPerm } = useAuth();
  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback((p = page, ps = pageSize, query = q, cat = categorie, st = statut) => {
    setLoading(true);
    API.get("/licenses", { params: { q: query, categorie: cat, statut: st, page: p, page_size: ps } })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, categorie, statut]);

  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openEdit = (lic) => { setEditing(lic.id); setForm(lic); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/licenses/${editing}`, form);
      toast.success("Permis mis a jour.");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="licenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Permis de conduire</h1>
          <p className="text-sm text-muted-foreground">Referentiel des permis et de leurs titulaires.</p>
        </div>
        {hasPerm("EXPORT_DATA") && <PrintButton testid="print-licenses" />}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Numero de permis..." className="pl-9 mono" data-testid="licenses-search" />
        </div>
        <Select value={categorie || "all"} onValueChange={(v) => { setCategorie(v === "all" ? "" : v); load(1, pageSize, q, v === "all" ? "" : v, statut); }}>
          <SelectTrigger className="w-40" data-testid="licenses-categorie"><SelectValue placeholder="Categorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes categories</SelectItem>
            {LIC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statut || "all"} onValueChange={(v) => { setStatut(v === "all" ? "" : v); load(1, pageSize, q, categorie, v === "all" ? "" : v); }}>
          <SelectTrigger className="w-40" data-testid="licenses-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {LICENSE_STATUTS.map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => load(1)} variant="secondary" data-testid="licenses-apply">Filtrer</Button>
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          {result && <PrintHeader title="Liste des permis de conduire" subtitle={`${result.total.toLocaleString("fr-FR")} enregistrement(s) — page ${page}`} />}
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Numero", "Titulaire", "Categorie", "Delivrance", "Expiration", "Statut", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((l, i) => (
                  <tr key={l.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`license-row-${i}`}>
                    <td className="px-3 py-2.5 mono text-xs font-semibold">{l.numero}</td>
                    <td className="px-3 py-2.5">{l.titulaire || "-"}</td>
                    <td className="px-3 py-2.5">{l.categorie}</td>
                    <td className="px-3 py-2.5 text-xs">{l.date_delivrance || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{l.date_expiration || "-"}</td>
                    <td className="px-3 py-2.5"><StatusBadge value={l.statut} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Button asChild variant="ghost" size="sm" data-testid={`license-driver-${i}`}>
                        <Link to={`/conducteurs/${l.driver_id}`}><Eye className="h-4 w-4" /></Link>
                      </Button>
                      {hasPerm("UPDATE_LICENSE") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`license-edit-${i}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {result && result.items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">Aucun permis trouve.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="licenses-pagination" />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="license-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le permis</DialogTitle>
          </DialogHeader>
          <LicenseForm initial={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="license-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="license-save">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
